import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelL } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';

import { CommonMode } from '@sorare/gameplay/src/components/commonGame/CommonMode';

import { InfoDialog } from 'components/commonGame/InfoDialog';

import { LimitedPlusMode } from './LimitedPlusMode';
import type { ChooseGameMode_conversionCredit } from './__generated__/index.graphql';

const Root = styled(Container)`
  min-height: 100%;
  --max-cell-width: calc(80 * var(--unit));
`;
const Header = styled(Vertical).attrs({ center: true, gap: 2 })`
  position: relative;
  width: 100%;
  max-width: var(--max-cell-width);
`;
const InnerWrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit) 0;
  align-items: center;
`;
const InfoButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const TapInstructionWrapper = styled.div`
  padding: var(--double-unit) var(--quadruple-unit);
  z-index: 1;
`;
const ButtonWrapper = styled.div`
  position: sticky;
  bottom: var(--double-unit);
  padding: 0 var(--quadruple-unit);
  align-self: center;
`;

const ItemsWrapper = styled(Vertical).attrs({ gap: 2 })`
  align-items: center;
  align-self: stretch;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
`;

type Props = {
  onGoPro: () => void;
  onChooseCommon?: () => void;
  goProMode?: boolean;
  conversionCredit: Nullable<ChooseGameMode_conversionCredit>;
  tapInstructionMessage: MessageDescriptor;
};
export const ChooseGameMode = ({
  onGoPro,
  onChooseCommon,
  goProMode,
  conversionCredit,
  tapInstructionMessage,
}: Props) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const track = useEvents();
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState<
    typeof Rarity.common | typeof Rarity.limited | null
  >(null);
  const commonSelected = selected === Rarity.common;
  const limitedSelected = selected === Rarity.limited;

  const showTapInstructions = !goProMode && !selected;
  const showSelectButton = !goProMode && selected;

  return (
    <>
      <Root>
        <StyledTextureBackground
          background="wave1"
          color="var(--c-blue-400)"
          mode="dark"
        />
        <InnerWrapper>
          <Header>
            <StarBall />
            <BrandL className="text-center">
              {goProMode ? (
                <FormattedMessage {...glossary.goPro} />
              ) : (
                <FormattedMessage
                  id="ChooseGameMode.title"
                  defaultMessage="Choose your game mode"
                />
              )}
            </BrandL>
            <InfoButtonWrapper>
              <IconButton
                disableDebounce
                color="transparent"
                size="small"
                onClick={() => {
                  setInfoDialogOpen(true);
                }}
                title={formatMessage(glossary.learnMore)}
                icon={faInfoCircle}
              />
            </InfoButtonWrapper>
          </Header>
          <ItemsWrapper gap={2} center>
            <LimitedPlusMode
              selected={limitedSelected}
              onSelect={() => {
                setSelected(Rarity.limited);
              }}
              onGoPro={onGoPro}
              conversionCredit={conversionCredit}
              goProMode={!!goProMode}
            />
            {!goProMode && (
              <CommonMode
                selected={commonSelected}
                onSelect={() => {
                  setSelected(Rarity.common);
                }}
              />
            )}
          </ItemsWrapper>
          {!!showTapInstructions && (
            <TapInstructionWrapper>
              <LabelL color="var(--c-nd-600)" className="text-center" bold>
                <FormattedMessage {...tapInstructionMessage} />
              </LabelL>
            </TapInstructionWrapper>
          )}
          {!!showSelectButton && (
            <ButtonWrapper>
              <OnboardingButton
                color="secondary"
                size="large"
                onClick={() => {
                  track('Click Continue In Choose Your Level', {
                    scarcity: selected,
                  });
                  if (commonSelected) {
                    onChooseCommon?.();
                  } else {
                    onGoPro();
                  }
                }}
              >
                <FormattedMessage {...glossary.confirm} />
              </OnboardingButton>
            </ButtonWrapper>
          )}
        </InnerWrapper>
      </Root>
      <InfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      />
    </>
  );
};

ChooseGameMode.fragments = {
  conversionCredit: gql`
    fragment ChooseGameMode_conversionCredit on ConversionCredit {
      id
      ...LimitedPlusMode_conversionCredit
    }
    ${LimitedPlusMode.fragments.conversionCredit}
  ` as TypedDocumentNode<ChooseGameMode_conversionCredit>,
};
